import React from "react"; // Import React for JSX
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import { Tooltip } from "@material-ui/core";

const PaginationBar = ({
  totalCount,
  currentPage,
  setPage,
  perPage = 10,
  setPerPage,
  perPageList = [10, 20, 30],
}) => {
  const totalPages = Math.ceil(totalCount / perPage);

  // Function to determine which page numbers to display, including ellipses
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 7; // Max number of visible page buttons (including 1, totalPages, and ellipses)

    if (totalPages <= maxPagesToShow) {
      // If total pages are few, show all of them
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // If total pages are many, apply ellipsis logic
      const leftBound = currentPage - 2;
      const rightBound = currentPage + 2;
      let pages = new Set();

      // Always add first page
      pages.add(1);

      // Add pages around current page
      for (let i = leftBound; i <= rightBound; i++) {
        if (i > 1 && i < totalPages) {
          pages.add(i);
        }
      }

      // Always add last page
      pages.add(totalPages);

      // Convert set to array and sort
      const sortedPages = Array.from(pages).sort((a, b) => a - b);

      // Add ellipses where there are gaps
      let finalPageNumbers = [];
      for (let i = 0; i < sortedPages.length; i++) {
        if (i > 0 && sortedPages[i] - sortedPages[i - 1] > 1) {
          finalPageNumbers.push("...");
        }
        finalPageNumbers.push(sortedPages[i]);
      }
      return finalPageNumbers;
    }
    return pageNumbers;
  };

  const displayedPageNumbers = getPageNumbers();

  const handlePageClick = (pageNumber) => {
    if (pageNumber !== "...") {
      setPage(pageNumber);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setPage(currentPage + 1);
    }
  };

  // Handle change in items per page
  const handlePerPageChange = (event) => {
    const newPerPage = Number(event.target.value);
    setPerPage(newPerPage);
    setPage(1); // Reset to first page when perPage changes
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "10px",
        margin: "20px 0",
        fontSize: 12,
      }}
    >
      {/* Dropdown for items per page */}
      <span style={{ color: "#666" }}>{totalCount} result(s) </span>
      {setPerPage && perPageList.length > 0 && (
        <Tooltip title="Results per page">
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <select
              value={perPage}
              onChange={handlePerPageChange}
              style={{
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                backgroundColor: "#f9f9f9",
                cursor: "pointer",
              }}
            >
              {perPageList.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </Tooltip>
      )}

      <PageNumber
        testId="previous-page"
        pageNo={<KeyboardArrowLeftRoundedIcon />}
        onClick={handlePrevious}
        disabled={currentPage === 1}
      />
      <div style={{ display: "flex", gap: "5px" }}>
        {displayedPageNumbers.map((pageNumber, index) => (
          <PageNumber // Removed index from key as pageNumber is unique and stable
            key={pageNumber} // Use pageNumber as key for stability
            pageNo={pageNumber}
            onClick={() => handlePageClick(pageNumber)}
            isActive={pageNumber === currentPage}
            isEllipsis={pageNumber === "..."}
          />
        ))}
      </div>
      <PageNumber
        testId="next-page"
        pageNo={<KeyboardArrowRightRoundedIcon />}
        onClick={handleNext}
        disabled={currentPage >= totalPages}
      />
    </div>
  );
};

const PageNumber = ({
  pageNo,
  onClick,
  isActive,
  disabled,
  isEllipsis,
  testId,
}) => {
  const baseStyle = {
    padding: "8px 12px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    cursor: "pointer",
    backgroundColor: "#f0f0f0",
    color: "#333",
    fontWeight: "normal",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "30px", // Ensure square-like appearance
    boxSizing: "border-box",
    userSelect: "none", // Prevent text selection
  };

  const activeStyle = isActive
    ? {
        backgroundColor: "#007bff",
        color: "#fff",
        borderColor: "#007bff",
        fontWeight: "bold",
      }
    : {};

  const disabledStyle = disabled
    ? {
        opacity: 0.5,
        cursor: "not-allowed",
      }
    : {};

  const ellipsisStyle = isEllipsis
    ? {
        cursor: "default",
        backgroundColor: "transparent",
        borderColor: "transparent",
        fontWeight: "bold",
      }
    : {};

  return (
    <div
      data-testid={testId}
      style={{
        ...baseStyle,
        ...activeStyle,
        ...disabledStyle,
        ...ellipsisStyle,
      }}
      onClick={!disabled && !isEllipsis ? onClick : undefined}
    >
      {pageNo}
    </div>
  );
};

export default PaginationBar;
